import {
  AccidentType,
  ViolationType,
  ClientFieldDataType,
  DriverFieldsType,
  GetDetailsResponseType,
  VehicleFieldsType,
  LienHolderFieldsType,
  DriverType,
  AddressFieldsType,
  ContactInfoFieldsType,
  GaragingAddressFieldsType,
  ParkingDetailsFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { isMissing } from 'Shared/utils';

// get specific value from FieldValueType
export function buildCoveragesByFieldValue(
  coverages: GetDetailsResponseType['coverages'],
  key: keyof ClientFieldDataType
) {
  return {
    policy: Object.keys(coverages.policy).reduce<
      Record<string, ClientFieldDataType[typeof key]>
    >(
      (acc, coverageKey) => ({
        ...acc,
        [coverageKey]: coverages.policy[coverageKey][key],
      }),
      {}
    ),
    vehicles: Object.keys(coverages.vehicles).reduce<
      Record<string, Record<string, ClientFieldDataType[typeof key]>>
    >(
      (vehicles, vehicleId) => ({
        ...vehicles,
        [vehicleId]: Object.keys(coverages.vehicles[vehicleId]).reduce<
          Record<string, ClientFieldDataType[typeof key]>
        >(
          (acc, coverageKey) => ({
            ...acc,
            [coverageKey]: coverages.vehicles[vehicleId][coverageKey][key],
          }),
          {}
        ),
      }),
      {}
    ),
  };
}

type AccidentDataByFieldValueType<T> = Record<
  string,
  Record<keyof Omit<AccidentType, 'identifier'>, T> & { identifier: string }
>;

type ViolationDataByFieldValueType<T> = Record<
  string,
  Record<keyof Omit<ViolationType, 'identifier'>, T> & {
    identifier: string;
  }
>;

export type DriverDataByFieldValueTypeRecordValue<T> = Record<
  keyof Omit<
    DriverFieldsType,
    | 'accidents'
    | 'violations'
    | 'identifier'
    | 'driverType'
    | 'editable'
    | 'discoveredDriver'
    | 'declinedSsn'
  >,
  T
> & {
  identifier: string;
  editable: boolean;
  driverType: DriverType;
  discoveredDriver: boolean;
  declinedSsn: boolean;
  accidents: AccidentDataByFieldValueType<T>;
  violations: ViolationDataByFieldValueType<T>;
};

export type DriverDataByFieldValueType<T> = Record<
  string,
  DriverDataByFieldValueTypeRecordValue<T>
>;

export const getDriverDataByFieldValue = <
  T extends 'value' | 'errors' | 'editable' | 'masked',
>(
  drivers: GetDetailsResponseType['drivers'],
  key: T,
  includeThirdPartyReportedIncidents: boolean = false
): DriverDataByFieldValueType<ClientFieldDataType[T]> => {
  return Object.keys(drivers).reduce<
    DriverDataByFieldValueType<ClientFieldDataType[T]>
  >((acc, driverId) => {
    const driver = drivers[driverId];
    acc[driverId] = {
      identifier: driver.identifier,
      editable: driver.editable,
      accidents: Object.keys(driver.accidents).reduce<
        AccidentDataByFieldValueType<ClientFieldDataType[T]>
      >((acc, incidentId) => {
        if (
          includeThirdPartyReportedIncidents ||
          driver.accidents[incidentId].month.source === 'user_reported'
        ) {
          acc[incidentId] = {
            identifier: driver.accidents[incidentId].identifier,
            atFault: driver.accidents[incidentId].atFault[key],
            lossType: driver.accidents[incidentId].lossType[key],
            accidentType: driver.accidents[incidentId].accidentType[key],
            day: driver.accidents[incidentId].day[key],
            month: driver.accidents[incidentId].month[key],
            year: driver.accidents[incidentId].year[key],
            typeOfLoss: driver.accidents[incidentId].typeOfLoss[key],
          };
        }
        return acc;
      }, {}),
      ageFirstLicensed: driver.ageFirstLicensed[key],
      coveredWithinLookbackPeriod: driver.coveredWithinLookbackPeriod[key],
      currentInsuranceBiLevel: driver.currentInsuranceBiLevel[key],
      currentInsuranceCarrierName: driver.currentInsuranceCarrierName[key],
      currentInsuranceInsuredDuration:
        driver.currentInsuranceInsuredDuration[key],
      currentInsuranceLapseDuration: driver.currentInsuranceLapseDuration[key],
      currentInsuranceLapseReason: driver.currentInsuranceLapseReason[key],
      currentInsuranceLapseReasonOther:
        driver.currentInsuranceLapseReasonOther[key],
      dateOfBirth: driver.dateOfBirth[key],
      discoveredDriver: driver.discoveredDriver,
      driverType: driver.driverType,
      declinedSsn: driver.declinedSsn,
      drivingCourseDate: driver.drivingCourseDate[key],
      educationLevel: driver.educationLevel[key],
      excludeFromQuote: driver.excludeFromQuote[key],
      exclusionReason: driver.exclusionReason[key],
      firstName: driver.firstName[key],
      gender: driver.gender[key],
      hasCompletedDrivingCourse: driver.hasCompletedDrivingCourse[key],
      isCurrentlyInsured: driver.isCurrentlyInsured[key],
      isDeployed: driver.isDeployed[key],
      isHomeOwner: driver.isHomeOwner[key],
      lastName: driver.lastName[key],
      licenseNumber: driver.licenseNumber[key],
      licensePreviouslySuspended: driver.licensePreviouslySuspended[key],
      licenseState: driver.licenseState[key],
      licenseStatus: driver.licenseStatus[key],
      licenseVerified: driver.licenseVerified[key],
      livesInHousehold: driver.livesInHousehold[key],
      hasAccessToVehicles: driver.hasAccessToVehicles[key],
      hasOwnInsurancePolicy: driver.hasOwnInsurancePolicy[key],
      inStateResidencyDuration: driver.inStateResidencyDuration[key],
      multipleHouseholdsMultipleStates:
        driver.multipleHouseholdsMultipleStates[key],
      outOfStateStudent: driver.outOfStateStudent[key],
      ownVehicleWhileOutOfStateStudent:
        driver.ownVehicleWhileOutOfStateStudent[key],
      hasForeignLicense: driver.hasForeignLicense[key],
      hasLearnersPermit: driver.hasLearnersPermit[key],
      userReportedStatus: driver.userReportedStatus[key],
      licenseReportStatus: driver.licenseReportStatus[key],
      maritalStatus: driver.maritalStatus[key],
      middleName: driver.middleName[key],
      militaryRank: driver.militaryRank[key],
      militaryServiceType: driver.militaryServiceType[key],
      previousSuspensionCount: driver.previousSuspensionCount[key],
      previousSuspensionDate: driver.previousSuspensionDate[key],
      profession: driver.profession[key],
      relationshipToApplicant: driver.relationshipToApplicant[key],
      ssn: driver.ssn[key],
      suffix: driver.suffix[key],
      isGoodStudent: driver.isGoodStudent[key],
      violations: Object.keys(driver.violations).reduce<
        ViolationDataByFieldValueType<ClientFieldDataType[T]>
      >((acc, incidentId) => {
        if (
          includeThirdPartyReportedIncidents ||
          driver.violations[incidentId].month.source === 'user_reported'
        ) {
          acc[incidentId] = {
            identifier: driver.violations[incidentId].identifier,
            month: driver.violations[incidentId].month[key],
            day: driver.violations[incidentId].day[key],
            sr22: driver.violations[incidentId].sr22[key],
            violationType: driver.violations[incidentId].violationType[key],
            year: driver.violations[incidentId].year[key],
          };
        }
        return acc;
      }, {}),
      cfrRequired: driver.cfrRequired[key],
      cfrCaseNumber: driver.cfrCaseNumber[key],
      cfrType: driver.cfrType[key],
      cfrIssueState: driver.cfrIssueState[key],
    };
    return acc;
  }, {});
};

export type ContactInfoByFieldValueType<T> = Record<
  string,
  Record<keyof ContactInfoFieldsType, T>
>;

export const getContactInfoByFieldValue = <
  T extends 'value' | 'errors' | 'editable',
>(
  contactInfo: GetDetailsResponseType['contactInfo'],
  key: T
): ContactInfoByFieldValueType<ClientFieldDataType[T]> => {
  return Object.keys(contactInfo).reduce<
    ContactInfoByFieldValueType<ClientFieldDataType[T]>
  >((acc, id) => {
    const data = contactInfo[id];
    acc[id] = {
      email: data.email[key],
      phoneNumber: data.phoneNumber[key],
      phoneType: data.phoneType[key],
    };
    return acc;
  }, {});
};

export const getContactInfoCompleteness = (
  contactInfoServerErrors?: ContactInfoByFieldValueType<
    ClientFieldDataType['errors']
  >
): Record<string, boolean> => {
  if (!contactInfoServerErrors) {
    return { contactInfo: false };
  }
  return Object.keys(contactInfoServerErrors).reduce<Record<string, boolean>>(
    (acc, driverId) => {
      const contactInfoFields = contactInfoServerErrors[driverId];
      const topLevelKeys = ['email', 'phoneNumber', 'phoneType'] as const;

      const hasServerErrorInCommonFields = topLevelKeys.find(
        (field) => contactInfoFields[field]?.length
      );

      return {
        ...acc,
        contactInfo: isMissing(hasServerErrorInCommonFields),
      };
    },
    {}
  );
};

export const getDriverDataCompleteness = (
  driverServerErrors: DriverDataByFieldValueType<ClientFieldDataType['errors']>
): Record<string, boolean> => {
  return Object.keys(driverServerErrors).reduce<Record<string, boolean>>(
    (acc, driverId) => {
      const driverFields = driverServerErrors[driverId];
      const topLevelKeys = [
        'ageFirstLicensed',
        'currentInsuranceBiLevel',
        'currentInsuranceCarrierName',
        'currentInsuranceLapseDuration',
        'currentInsuranceLapseReason',
        'currentInsuranceLapseReasonOther',
        'dateOfBirth',
        'drivingCourseDate',
        'educationLevel',
        'exclusionReason',
        'firstName',
        'gender',
        'hasCompletedDrivingCourse',
        'isCurrentlyInsured',
        'isDeployed',
        'isHomeOwner',
        'lastName',
        'licenseNumber',
        'licensePreviouslySuspended',
        'licenseState',
        'licenseVerified',
        'userReportedStatus',
        'maritalStatus',
        'middleName',
        'militaryRank',
        'militaryServiceType',
        'previousSuspensionCount',
        'previousSuspensionDate',
        'profession',
        'relationshipToApplicant',
        'suffix',
        'isGoodStudent',
        'hasAccessToVehicles',
        'livesInHousehold',
        'hasOwnInsurancePolicy',
        'hasForeignLicense',
        'hasLearnersPermit',
        'inStateResidencyDuration',
        'multipleHouseholdsMultipleStates',
        'outOfStateStudent',
        'ownVehicleWhileOutOfStateStudent',
        'cfrRequired',
        'cfrCaseNumber',
        'cfrType',
        'cfrIssueState',
        'coveredWithinLookbackPeriod',
      ] as const;

      const notApplicableToExcludedAndListOnlyDriver = [
        'hasAccessToVehicles',
        'livesInHousehold',
        'hasOwnInsurancePolicy',
        'inStateResidencyDuration',
        'outOfStateStudent',
        'ownVehicleWhileOutOfStateStudent',
      ];

      const hasServerErrorInCommonFields = topLevelKeys.find((field) => {
        if (
          (driverFields.driverType === 'listOnly' ||
            driverFields.driverType === 'excludedPerson') &&
          notApplicableToExcludedAndListOnlyDriver.includes(field)
        ) {
          return 0;
        }
        return driverFields[field]?.length;
      });

      const hasErrorsInAccidents = Object.keys(driverFields.accidents).find(
        (accidentId) => {
          const accident = driverFields.accidents[accidentId];
          const accidentFields = [
            'atFault',
            'lossType',
            'accidentType',
            'month',
            'year',
            'typeOfLoss',
          ] as const;

          return accidentFields.find((field) => accident[field]?.length);
        }
      );

      const hasErrorsInViolations = Object.keys(driverFields.violations).find(
        (violationId) => {
          const violation = driverFields.violations[violationId];
          const violationFields = [
            'month',
            'sr22',
            'violationType',
            'year',
          ] as const;

          return violationFields.find((field) => violation[field]?.length);
        }
      );

      return {
        ...acc,
        [driverId]:
          isMissing(hasServerErrorInCommonFields) &&
          isMissing(hasErrorsInAccidents) &&
          isMissing(hasErrorsInViolations),
      };
    },
    {}
  );
};

export const getViolationsByFieldValue = (
  violations: GetDetailsResponseType['drivers'][0]['violations']
) => {
  return Object.keys(violations).reduce<
    Record<string, Record<keyof ViolationType, string>>
  >((acc, incidentId) => {
    acc[incidentId] = {
      identifier: violations[incidentId].identifier,
      month: violations[incidentId].month.value,
      day: violations[incidentId].day.value,
      sr22: violations[incidentId].sr22.value,
      violationType: violations[incidentId].violationType.value,
      year: violations[incidentId].year.value,
    };
    return acc;
  }, {});
};

export const getAccidentsByFieldValue = (
  accidents: GetDetailsResponseType['drivers'][0]['accidents']
) => {
  return Object.keys(accidents).reduce<
    Record<string, Record<keyof AccidentType, string>>
  >((acc, incidentId) => {
    acc[incidentId] = {
      identifier: accidents[incidentId].identifier,
      atFault: accidents[incidentId].atFault.value,
      lossType: accidents[incidentId].lossType.value,
      accidentType: accidents[incidentId].accidentType.value,
      day: accidents[incidentId].day.value,
      month: accidents[incidentId].month.value,
      year: accidents[incidentId].year.value,
      typeOfLoss: accidents[incidentId].typeOfLoss.value,
    };
    return acc;
  }, {});
};

export type AddressDataFieldValueType<T> = Record<
  string,
  Record<keyof Omit<AddressFieldsType, 'identifier'>, T> & {
    identifier: string;
  }
>;

export const getAddressDataByFieldValue = <T extends 'value' | 'editable'>(
  address: GetDetailsResponseType['address'],
  key: T
): AddressDataFieldValueType<ClientFieldDataType[T]> => {
  return Object.keys(address).reduce<
    AddressDataFieldValueType<ClientFieldDataType[T]>
  >((acc, id) => {
    const data = address[id];
    acc[id] = {
      identifier: data.identifier,
      address1: data.address1[key],
      address2: data.address2[key],
      city: data.city[key],
      state: data.state[key],
      zip: data.zip[key],
    };
    return acc;
  }, {});
};

export type VehicleDataByFieldValueType<T> = Record<
  string,
  Record<
    keyof Omit<
      VehicleFieldsType,
      | 'lienHolder'
      | 'identifier'
      | 'editable'
      | 'garagingAddress'
      | 'parkingDetails'
    >,
    T
  > & {
    lienHolder: Record<keyof LienHolderFieldsType, T>;
    garagingAddress: Record<keyof GaragingAddressFieldsType, T>;
    parkingDetails: Record<keyof ParkingDetailsFieldsType, T>;
    identifier: string;
    editable: boolean;
  }
>;

export const getVehicleDataByFieldValue = <
  T extends 'value' | 'errors' | 'editable',
>(
  vehicles: GetDetailsResponseType['vehicles'],
  key: T
): VehicleDataByFieldValueType<ClientFieldDataType[T]> => {
  return Object.keys(vehicles).reduce<
    VehicleDataByFieldValueType<ClientFieldDataType[T]>
  >((acc, vehicleId) => {
    const vehicle = vehicles[vehicleId];
    acc[vehicleId] = {
      identifier: vehicle.identifier,
      editable: vehicle.editable,
      activeDisable: vehicle.activeDisable[key],
      alarm: vehicle.alarm[key],
      annualMileage: vehicle.annualMileage[key],
      bodyStyle: vehicle.bodyStyle[key],
      lienHolder: {
        name: vehicle.lienHolder.name[key],
        addressLine1: vehicle.lienHolder.addressLine1[key],
        addressLine2: vehicle.lienHolder.addressLine2[key],
        city: vehicle.lienHolder.city[key],
        postalCode: vehicle.lienHolder.postalCode[key],
        state: vehicle.lienHolder.state[key],
      },
      make: vehicle.make[key],
      model: vehicle.model[key],
      ownershipType: vehicle.ownershipType[key],
      parkingDetails: {
        inStateParkingDuration:
          vehicle.parkingDetails.inStateParkingDuration[key],
        parkingLocationReason:
          vehicle.parkingDetails.parkingLocationReason[key],
      },
      parkingType: vehicle.parkingType[key],
      registeredToApplicant: vehicle.registeredToApplicant[key],
      usedInMexico: vehicle.usedInMexico[key],
      availableForNonCoveredDrivers: vehicle.availableForNonCoveredDrivers[key],
      emergencyServices: vehicle.emergencyServices[key],
      feeBasedServices: vehicle.feeBasedServices[key],
      hazmatToolTransport: vehicle.hazmatToolTransport[key],
      usedAsResidence: vehicle.usedAsResidence[key],
      passiveDisable: vehicle.passiveDisable[key],
      recovery: vehicle.recovery[key],
      rideshareUse: vehicle.rideshareUse[key],
      totalMileage: vehicle.totalMileage[key],
      usage: vehicle.usage[key],
      vehicleServicesId: vehicle.vehicleServicesId[key],
      partialVin: vehicle.partialVin[key],
      vin: vehicle.vin[key],
      year: vehicle.year[key],
      garagingAddress: {
        addressLine1: vehicle.garagingAddress.addressLine1[key],
        addressLine2: vehicle.garagingAddress.addressLine2[key],
        city: vehicle.garagingAddress.city[key],
        postalCode: vehicle.garagingAddress.postalCode[key],
        state: vehicle.garagingAddress.state[key],
        garagingAddressOutcome:
          vehicle.garagingAddress.garagingAddressOutcome[key],
      },
      garagedAtPrimaryAddress: vehicle.garagedAtPrimaryAddress[key],
      hasAntiTheftDevice: vehicle.hasAntiTheftDevice[key],
      businessUse: vehicle.businessUse[key],
      businessUseType: vehicle.businessUseType[key],
    };
    return acc;
  }, {});
};

export const getVehicleDataCompleteness = (
  vehicleServerErrors: VehicleDataByFieldValueType<
    ClientFieldDataType['errors']
  >
): Record<string, boolean> => {
  return Object.keys(vehicleServerErrors).reduce<Record<string, boolean>>(
    (acc, vehicleId) => {
      const vehicleErrors = vehicleServerErrors[vehicleId];
      const commonFields = [
        'activeDisable',
        'alarm',
        'annualMileage',
        'bodyStyle',
        'make',
        'model',
        'ownershipType',
        'parkingType',
        'passiveDisable',
        'recovery',
        'rideshareUse',
        'registeredToApplicant',
        'totalMileage',
        'usage',
        'vehicleServicesId',
        'partialVin',
        'vin',
        'year',
        'usedInMexico',
        'availableForNonCoveredDrivers',
        'feeBasedServices',
        'hazmatToolTransport',
        'usedAsResidence',
        'emergencyServices',
        'hasAntiTheftDevice',
        'businessUse',
        'businessUseType',
      ] as const;

      const hasServerErrorInCommonFields = commonFields.find(
        (field) => vehicleErrors[field]?.length
      );

      const lienholderFields = [
        'name',
        'addressLine1',
        'addressLine2',
        'city',
        'postalCode',
        'state',
      ] as const;

      const hasErrorInLienholder = lienholderFields.find(
        (lienholderField) => vehicleErrors.lienHolder[lienholderField]?.length
      );

      const garagingAddressFields = ['garagedAtPrimaryAddress'] as const;

      const garagingAddressFormFields = [
        'addressLine1',
        'addressLine2',
        'city',
        'postalCode',
        'state',
        'garagingAddressOutcome',
      ] as const;

      const hasGaragingAddressError =
        garagingAddressFields.find((field) => vehicleErrors[field]?.length) ||
        garagingAddressFormFields.find(
          (field) => vehicleErrors.garagingAddress[field]?.length
        );

      const parkingDetailsFields = [
        'inStateParkingDuration',
        'parkingLocationReason',
      ] as const;

      const hasParkingDetailsError = parkingDetailsFields.find(
        (field) => vehicleErrors.parkingDetails[field]?.length
      );

      return {
        ...acc,
        [vehicleId]:
          isMissing(hasServerErrorInCommonFields) &&
          isMissing(hasErrorInLienholder) &&
          isMissing(hasGaragingAddressError) &&
          isMissing(hasParkingDetailsError),
      };
    },
    {}
  );
};

export const generateFieldDataTestId = (fieldName: string) =>
  fieldName.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
